import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { endOfMonth, format, startOfMonth } from 'date-fns'
import { BarChart3 } from 'lucide-react'
import { Cell, Pie, PieChart } from 'recharts'
import { type GiftCardInstanceType } from 'types/gifting'
import { Button } from 'tailwind-v4/components/button'
import { Skeleton } from 'tailwind-v4/components/skeleton'
import {
  DateRangeControl,
  type DateRangeValue,
} from 'tailwind-v4/blocks/date-range-control'
import { Page } from 'tailwind-v4/blocks/page'
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/shadcn/ui/chart'
import useGlobal from '@/hooks/useGlobal'
import morpheusApi from '@/state/morpheus/morpheus.slice'

/**
 * The gifting report types. They used to live in
 * `@/connect-types/analytics/giftingAnalytics.type`, which was deleted in a
 * dead-code sweep while the imports stayed behind, so the whole report was
 * silently `any`. Restated here, the report page's own home, and shared with
 * the cards table next door.
 */
export type GiftStatusType = GiftCardInstanceType['status']

/** One status bucket of a gifting report: how many, how much, on average. */
export interface GiftReportTotal {
  count: number
  total_amount: number
  formatted_amount: string
  formatted_average: string
  average: number
  name: GiftStatusType | 'manual'
}

export interface GiftReportTotals {
  active: GiftReportTotal
  unpaid: GiftReportTotal
  redeemed: GiftReportTotal
  refunded: GiftReportTotal
  /** Only present when a card was activated by hand. */
  manual?: GiftReportTotal
}

export interface GiftReportItem {
  id: string
  name: string
  totals: GiftReportTotals
}

export type GiftReportResponse = Record<string, GiftReportItem>

/**
 * Gift-card status colours, as design-system tokens rather than raw hex, so
 * they flip with the theme. Exported so any other gifting surface that marks a
 * card by status reads from the same palette.
 */
export const COLORS: Record<GiftStatusType, string> = {
  active: 'var(--success)',
  redeemed: 'var(--warning)',
  unpaid: 'var(--destructive)',
  refunded: 'var(--info)',
  deactivated: 'var(--muted-foreground)',
}

/**
 * The buckets the reporting endpoint splits a card into, in reading order.
 * `manual` is deliberately absent: it double-counts cards that already appear
 * under a status.
 */
const REPORT_STATUSES = ['active', 'redeemed', 'unpaid', 'refunded'] as const

type ReportStatus = (typeof REPORT_STATUSES)[number]

const chartConfig = {
  active: { label: 'Active', color: COLORS.active },
  redeemed: { label: 'Redeemed', color: COLORS.redeemed },
  unpaid: { label: 'Unpaid', color: COLORS.unpaid },
  refunded: { label: 'Refunded', color: COLORS.refunded },
} satisfies ChartConfig

/** The aggregate row the endpoint prepends; it has no card to configure. */
const AGGREGATE_NAME = 'All Cards'

export const secondsEpoch = (epoch_number: number) => {
  return Math.abs(epoch_number).toString().length > 10
    ? Math.floor(epoch_number / 1000)
    : epoch_number
}

interface ReportSlice {
  status: ReportStatus
  /** `nameKey` for the chart: resolves the label and colour off the config. */
  name: ReportStatus
  label: string
  /** Pence, so the pie can size the slices. */
  value: number
  count: number
  formatted: string
  formattedAverage: string
}

function buildSlices(totals: GiftReportTotals): ReportSlice[] {
  return REPORT_STATUSES.map((status) => {
    const bucket: GiftReportTotal | undefined = totals[status]
    return {
      status,
      name: status,
      label: chartConfig[status].label,
      value: Number(bucket?.total_amount ?? 0),
      count: bucket?.count ?? 0,
      formatted: bucket?.formatted_amount ?? '',
      formattedAverage: bucket?.formatted_average ?? '',
    }
  })
}

function formatRange(range: DateRangeValue) {
  return `${format(range.from, 'd MMM')} – ${format(range.to, 'd MMM yyyy')}`
}

/**
 * Tooltip value for a pie slice. The API formats every amount in the card's
 * own currency, so show that rather than reformatting the raw pence ourselves.
 */
function sliceValueFormatter(slices: ReportSlice[]) {
  return (value: unknown, name: unknown) => {
    const slice = slices.find((entry) => entry.name === String(name))
    // An empty string means the API sent no formatted amount for the bucket.
    if (slice?.formatted) return slice.formatted
    return Number(value).toLocaleString()
  }
}

/**
 * One status figure: small muted label above a semibold tabular value at body
 * size, with the counts underneath. No box, no big number: the house stat
 * treatment. The grid parent owns the spacing between them.
 */
function StatusStat({ slice }: { slice: ReportSlice }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="flex items-center gap-2 text-xs text-muted-foreground">
        <span
          aria-hidden
          className="size-2 shrink-0 rounded-full"
          style={{ backgroundColor: COLORS[slice.status] }}
        />
        {slice.label}
      </span>
      <span className="font-semibold tabular-nums text-foreground">
        {slice.formatted || '£0'}
      </span>
      <span className="text-xs text-muted-foreground tabular-nums">
        {slice.count.toLocaleString()} cards
        {slice.formattedAverage ? ` · ${slice.formattedAverage} avg` : null}
      </span>
    </div>
  )
}

/** The stat grid + chart, as skeletons, so loading keeps the loaded layout. */
function ReportSkeleton() {
  return (
    <section className="flex flex-col gap-6">
      <Skeleton className="h-5 w-40" />
      <div className="grid grid-cols-2 gap-6 @2xl:grid-cols-4">
        {REPORT_STATUSES.map((status) => (
          <div key={status} className="flex flex-col gap-1">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-5 w-20" />
            <Skeleton className="h-4 w-24" />
          </div>
        ))}
      </div>
      <Skeleton className="h-72 w-full rounded-xl" />
    </section>
  )
}

function GiftingReport() {
  const { orgId } = useGlobal()
  const navigate = useNavigate()
  const [dateRange, setDateRange] = useState<DateRangeValue>({
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date()),
  })

  const { data, isLoading } = morpheusApi.useGetGiftReportsQuery({
    orgId,
    start: dateRange.from,
    end: dateRange.to,
  })

  const reports = useMemo(() => {
    const report: GiftReportResponse = data ?? {}
    return Object.values(report)
  }, [data])

  const rangeLabel = formatRange(dateRange)
  const isEmpty = !isLoading && reports.length === 0

  return (
    <Page
      className="new-ui-theme bg-background text-foreground"
      title="Gifting reports"
      meta={<span className="text-sm text-muted-foreground">{rangeLabel}</span>}
      toolbar={
        <DateRangeControl
          value={dateRange}
          onChange={setDateRange}
          // The block ships at h-11/h-10 for a 44px tap target. That reads as
          // oversized furniture above a dense report with a mouse, so compact
          // it to the toolbar's h-8 from `sm` up and leave the phone alone.
          className="sm:[&_button.h-11]:h-8 sm:[&_button.size-11]:size-8"
        />
      }
    >
      {isEmpty ? (
        <div className="flex flex-col items-center gap-4 py-16 text-center">
          <div className="flex size-10 items-center justify-center rounded-xl border border-border text-muted-foreground">
            <BarChart3 className="size-5" />
          </div>
          <div className="flex flex-col gap-1">
            <h2 className="text-base font-semibold text-foreground">
              No gift card data
            </h2>
            <p className="max-w-sm text-sm text-muted-foreground">
              Nothing was sold or redeemed in this period. Create a gift card,
              or pick a wider date range.
            </p>
          </div>
        </div>
      ) : null}

      {isLoading ? (
        <div className="flex flex-col gap-12">
          <ReportSkeleton />
          <ReportSkeleton />
        </div>
      ) : null}

      {!isLoading && !isEmpty ? (
        <div className="flex flex-col gap-12">
          {reports.map((report) => {
            const slices = buildSlices(report.totals)
            const funded = slices.filter((slice) => slice.value > 0)

            return (
              <section key={report.id} className="flex flex-col gap-6">
                <div className="flex items-center justify-between gap-4">
                  <h2 className="text-sm font-semibold text-foreground">
                    {report.name}
                  </h2>
                  {report.name === AGGREGATE_NAME ? null : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        navigate(`/${orgId}/gifting/${report.id}`)
                      }}
                    >
                      Setup
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-6 @2xl:grid-cols-4">
                  {slices.map((slice) => (
                    <StatusStat key={slice.status} slice={slice} />
                  ))}
                </div>

                {funded.length > 0 ? (
                  <div className="flex flex-col gap-4 rounded-xl border border-border p-4">
                    <span className="text-xs text-muted-foreground">
                      Value by status
                    </span>
                    <ChartContainer
                      config={chartConfig}
                      className="h-72 w-full"
                    >
                      <PieChart>
                        <ChartTooltip
                          content={
                            <ChartTooltipContent
                              hideLabel
                              nameKey="name"
                              formatter={sliceValueFormatter(funded)}
                            />
                          }
                        />
                        <Pie
                          data={funded}
                          dataKey="value"
                          nameKey="name"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          strokeWidth={0}
                        >
                          {funded.map((slice) => (
                            <Cell
                              key={slice.status}
                              fill={`var(--color-${slice.status})`}
                            />
                          ))}
                        </Pie>
                        {/* Four labels don't fit one phone row, so let them
                            wrap rather than run off the chart. */}
                        <ChartLegend
                          content={<ChartLegendContent className="flex-wrap" />}
                        />
                      </PieChart>
                    </ChartContainer>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No value on this card in this period.
                  </p>
                )}
              </section>
            )
          })}
        </div>
      ) : null}
    </Page>
  )
}

export default GiftingReport
